import { mkdir, rm, stat, writeFile } from "node:fs/promises";
import { relative, resolve } from "node:path";
import { build } from "vite";
import { scanRoutes } from "../router/route-scanner.js";
import type { Adapter } from "./index.js";
import { writeSsrEntry } from "./shared.js";
import { DEFAULT_CAPABILITIES } from "../runtime/capabilities.js";

/**
 * Node adapter for nix-js-kit.
 *
 * Produces a self-contained Node server entry at `.nix-js/node-server.mjs`.
 * Run it with:
 *
 *   node .nix-js/node-server.mjs
 *
 * The server serves static files from `dist/` and renders pages on demand for
 * unmatched routes.
 */
export const nodeAdapter: Adapter = {
  name: "node",
  capabilities: DEFAULT_CAPABILITIES,

  async build(options) {
    const root = resolve(options.root);
    const outDir = resolve(root, options.outDir);
    const generatedDir = resolve(root, ".nix-js");

    try {
      await stat(outDir);
    } catch {
      throw new Error(
        `Output directory not found: ${outDir}. Run "nix-js-kit build" first.`,
      );
    }

    await rm(generatedDir, { recursive: true, force: true });
    await mkdir(generatedDir, { recursive: true });

    const appDir = resolve(root, options.appDir);
    const routes = await scanRoutes(appDir);

    const entryPath = resolve(generatedDir, "node-index.ts");
    await writeSsrEntry(entryPath, routes, options);

    const serverPath = resolve(generatedDir, "node-server.ts");
    await writeFile(
      serverPath,
      buildNodeServerSource(relativeToUrlPath(generatedDir, outDir), options),
      "utf8",
    );

    await build({
      configFile: false,
      root,
      build: {
        outDir: generatedDir,
        emptyOutDir: false,
        ssr: true,
        lib: {
          entry: serverPath,
          formats: ["es"],
        },
        rollupOptions: {
          external: [/^@deijose\/nix-js-kit(?:\/.*)?$/, /^@deijose\/nix-js(?:\/.*)?$/, /^node:/],
          output: {
            entryFileNames: "node-server.mjs",
            inlineDynamicImports: true,
          },
        },
      },
    });
  },
};

function buildNodeServerSource(
  outDirUrl: string,
  options: {
    clientEntry: string;
    lang: string;
    port?: number;
  },
): string {
  return `// AUTO-GENERATED by @deijose/nix-js-kit. Do not edit.
import { createServer } from "node:http";
import { fileURLToPath } from "node:url";
import { createWebHandler } from "@deijose/nix-js-kit/runtime";
import { incomingMessageToRequest } from "@deijose/nix-js-kit/runtime";
import handler from "./node-index.ts";

const outDir = fileURLToPath(new URL(${JSON.stringify(outDirUrl)}, import.meta.url));
const port = Number(process.env.PORT) || ${options.port ?? 3000};

// The adapter-bundled handler already handles actions, API routes, render
// endpoint and dynamic SSR. We only need to add static file serving from
// the output directory, then fall through to the bundled handler.
const webHandler = createWebHandler(
  { pages: [], api: [], error404: undefined, error500: undefined },
  {},
  { staticRoot: outDir, lang: ${JSON.stringify(options.lang)}, clientEntry: ${JSON.stringify(options.clientEntry)} },
);

createServer(async (req, res) => {
  const body = req.method !== "GET" && req.method !== "HEAD"
    ? await readBody(req)
    : undefined;
  const request = incomingMessageToRequest(req, body);
  // Try static files first via the unified handler, then fall back to the
  // bundled SSR handler for dynamic routes.
  let response = await webHandler(request);
  if (response.status === 404) {
    response = await handler(request);
  }
  res.writeHead(response.status, Object.fromEntries(response.headers.entries()));
  res.end(Buffer.from(await response.arrayBuffer()));
}).listen(port, () => {
  console.log(\`Node server running at http://localhost:\${port}\`);
});

function readBody(req: import("node:http").IncomingMessage): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    req.on("data", (chunk) => chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk)));
    req.on("end", () => resolve(Buffer.concat(chunks)));
    req.on("error", reject);
  });
}
`;
}

function relativeToUrlPath(from: string, to: string): string {
  const path = relative(from, to).split("\\").join("/");
  return `${path.startsWith(".") ? path : `./${path}`}/`;
}
